if (!customElements.get("cart-upsell")) {
  class CartUpsell extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.slider = null;
      this.drawer = null;
      this.fetchAbort = null;
      this.refreshTimer = null;
      this.fallbackHtml = null;
      this.cache = new Map();
      this.cartUpdateEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) ||
        "cart-update";
      this.onCartUpdate = this.onCartUpdate.bind(this);
    }

    connectedCallback() {
      this.cacheElements();
      this.bindDrawerRefresh();
      document.addEventListener(this.cartUpdateEvent, this.onCartUpdate);
      this.refresh();
    }

    disconnectedCallback() {
      document.removeEventListener(this.cartUpdateEvent, this.onCartUpdate);
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = null;
      }
      if (this.fetchAbort) {
        this.fetchAbort.abort();
        this.fetchAbort = null;
      }
    }

    get mode() {
      return this.dataset.upsellMode || "manual";
    }

    get limit() {
      return Number.parseInt(this.dataset.upsellLimit || "3", 10) || 3;
    }

    // How many cart products are used as seeds for the recommendations.
    get seedLimit() {
      return Number.parseInt(this.dataset.upsellSeedLimit || "3", 10) || 3;
    }

    get intents() {
      return (this.dataset.upsellIntents || "complementary,related")
        .split(",")
        .map((intent) => intent.trim())
        .filter(Boolean);
    }

    cacheElements() {
      this.track = this.querySelector("[data-upsell-track]");
      this.slider = this.querySelector("slider-component");
      this.drawer = this.closest("cart-drawer");
      if (this.track && this.fallbackHtml === null) {
        const fallback = this.querySelector("template[data-upsell-fallback]");
        this.fallbackHtml = fallback
          ? fallback.innerHTML
          : this.track.innerHTML;
      }
    }

    bindDrawerRefresh() {
      if (!this.drawer) return;
      if (typeof this.drawer.refreshUpsell === "function") return;
      this.drawer.refreshUpsell = () => {
        const upsell = this.drawer.querySelector("cart-upsell[data-upsell]");
        if (upsell && typeof upsell.refresh === "function") {
          upsell.refresh();
        }
      };
    }

    async onCartUpdate(event) {
      const variantId = event?.detail?.variantId;
      if (variantId) {
        this.dataset.upsellRecentVariantId = String(variantId);
      }
      this.scheduleRefresh();
    }

    scheduleRefresh() {
      if (this.mode !== "auto") return;
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null;
        this.refresh();
      }, 50);
    }

    async refresh() {
      this.cacheElements();
      if (!this.track) return;
      if (this.mode !== "auto") return;

      const cart = await this.getCart();
      const cartProductIds = this.getCartProductIds(cart);
      const seeds = this.getSeeds(cart, cartProductIds);

      if (!seeds.length) {
        this.renderFallback();
        return;
      }

      await this.loadRecommendations(seeds, cartProductIds);
    }

    async getCart() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
          credentials: "same-origin",
        });
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        return null;
      }
    }

    // Every product currently in the cart, so we never recommend what is already there.
    getCartProductIds(cart) {
      if (Array.isArray(cart?.items)) {
        return [
          ...new Set(
            cart.items
              .map((item) => item?.product_id)
              .filter(Boolean)
              .map(String)
          ),
        ];
      }
      return (this.dataset.upsellCartProducts || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
    }

    // Cart products used to ask Shopify for recommendations, most relevant first.
    getSeeds(cart, cartProductIds) {
      const seeds = [];
      const recentVariantId = this.dataset.upsellRecentVariantId;
      if (recentVariantId && Array.isArray(cart?.items)) {
        const recentItem = cart.items.find(
          (item) => String(item?.variant_id) === String(recentVariantId)
        );
        if (recentItem?.product_id) seeds.push(String(recentItem.product_id));
      }
      const fallbackSeed = this.dataset.upsellProductId;
      if (fallbackSeed && !cartProductIds.length) seeds.push(String(fallbackSeed));

      cartProductIds.forEach((id) => {
        if (!seeds.includes(id)) seeds.push(id);
      });

      return seeds.slice(0, this.seedLimit);
    }

    async loadRecommendations(seeds, cartProductIds) {
      if (!this.track) return;
      if (this.fetchAbort) this.fetchAbort.abort();
      const controller = new AbortController();
      this.fetchAbort = controller;

      const limit = this.limit;
      const seen = new Set(cartProductIds.map(String));
      const collected = [];

      try {
        for (const intent of this.intents) {
          const groups = await Promise.all(
            seeds.map((seed) =>
              this.fetchRecommendations(seed, intent, controller.signal)
            )
          );
          if (controller.signal.aborted) return;

          // Interleave the results so every cart product is represented.
          const longest = groups.reduce(
            (max, group) => Math.max(max, group.length),
            0
          );
          for (let index = 0; index < longest && collected.length < limit; index += 1) {
            for (const group of groups) {
              if (collected.length >= limit) break;
              const item = group[index];
              if (!item) continue;
              const productId = item.dataset?.upsellProduct;
              if (productId) {
                if (seen.has(productId)) continue;
                seen.add(productId);
              }
              collected.push(item);
            }
          }
          if (collected.length >= limit) break;
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
      }

      if (controller.signal.aborted) return;

      if (!collected.length) {
        this.renderFallback();
        return;
      }

      this.classList.remove("hidden");
      this.track.innerHTML = collected.map((item) => item.outerHTML).join("");
      this.afterRender();
    }

    async fetchRecommendations(productId, intent, signal) {
      const sectionId =
        this.dataset.upsellSectionId || "cart-upsell-recommendations";
      const cacheKey = `${productId}|${intent}|${sectionId}`;
      if (this.cache.has(cacheKey)) {
        return this.cache
          .get(cacheKey)
          .map((item) => item.cloneNode(true));
      }

      try {
        const rawUrl =
          this.dataset.url ||
          `${window.Shopify?.routes?.root || "/"}recommendations/products`;
        const url = new URL(rawUrl, window.location.origin);
        url.searchParams.set("section_id", sectionId);
        url.searchParams.set("product_id", productId);
        // Ask for a few extra so cart products can be filtered out afterwards.
        url.searchParams.set("limit", String(Math.min(10, this.limit + 4)));
        url.searchParams.set("intent", intent);

        const fetchHtml = window.webvista?.fetchHtml;
        const parsed = fetchHtml
          ? await fetchHtml(url.toString(), signal)
          : new DOMParser().parseFromString(
              await fetch(url.toString(), { signal }).then((response) => {
                if (!response.ok) {
                  throw new Error("Upsell recommendations failed.");
                }
                return response.text();
              }),
              "text/html"
            );

        const items = parsed.querySelector("[data-upsell-items]");
        const elements = items ? Array.from(items.children) : [];
        this.cache.set(cacheKey, elements);
        return elements.map((item) => item.cloneNode(true));
      } catch (error) {
        if (error?.name === "AbortError") throw error;
        return [];
      }
    }

    renderFallback() {
      if (!this.track) return;
      if (!this.fallbackHtml || !this.fallbackHtml.trim()) {
        this.classList.add("hidden");
        return;
      }
      this.classList.remove("hidden");
      this.track.innerHTML = this.fallbackHtml;
      this.afterRender();
    }

    afterRender() {
      if (window.webvista?.initLazyImageAnimation) {
        window.webvista.initLazyImageAnimation(this.track);
      }
      this.resetSlider();
    }

    resetSlider() {
      if (!this.slider) return;
      if (typeof this.slider.disconnectedCallback === "function") {
        this.slider.disconnectedCallback();
      }
      if (typeof this.slider.connectedCallback === "function") {
        this.slider.connectedCallback();
      }
    }
  }

  customElements.define("cart-upsell", CartUpsell);
}
